import { Box } from "@mui/material";
import { ReactComponent as Add } from "assets/add-1.svg";
import Button from "components/common/Button";
import Feedback from "components/common/Feedback";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { feedbackQuestionCategoryTypes, responseTypes } from "utils/Types";
import { routes } from "utils/routes";
import {
  fetchFeedbackFormDetails,
  fetchFeedbackFormsQuestionsCategories,
  reset,
  updateFeedback
} from "./Feedback.slice";
import Question from "./Question";
import "./index.scss";

const DynamicInput: React.FC<any> = (props: any) => {
  return <Question {...props} />;
};

export default function EditFeedback() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();

  const methods = useForm<any>();
  const { setValue, getValues } = methods;

  const questionCategories = useAppSelector((state) => state.feedback.questionCategories.list);
  const { status, ...formDetails } = useAppSelector((state) => state.feedback.formDetails);
  const { type, triggerPoint, questions: existingQuestions } = useAppSelector((state) => state.feedback.formDetails);

  const [questions, setQuestions] = useState<any>(existingQuestions || []);

  useEffect(() => {
    setQuestions(existingQuestions);
    setValue("questions", existingQuestions);
  }, [existingQuestions]);

  useEffect(() => {
    if (!questionCategories.length) dispatch(fetchFeedbackFormsQuestionsCategories());
    dispatch(fetchFeedbackFormDetails({ id: params.id }));
    return () => {
      dispatch(reset());
    };
  }, []);

  const addInput = () => {
    if (questions?.length > 5) {
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          variant: "error",
          setState: setFeedbackDetails({ shouldShow: false }),
          message: "Only 6 questions can be added."
        })
      );
    } else {
      const newQuestions = [...(getValues("questions") || []), { id: Date.now(), isNew: true }];
      setQuestions(newQuestions);
      setValue("questions", newQuestions);
    }
  };

  const deleteInput = (id: any) => {
    const newQuestions = getValues("questions").filter((_: any, index: any) => index !== id);
    setQuestions(newQuestions); // Remove item at index n
    setValue("questions", newQuestions);
  };

  const submitHandler = async (data: any) => {
    const questionsPayload = data.questions.map((question: any, index: number) => {
      switch (question.categoryId) {
        case feedbackQuestionCategoryTypes["Open Text"]:
        case feedbackQuestionCategoryTypes.Rating: {
          const payloadData = { ...formDetails.questions[index], ...question };
          delete payloadData.answers;
          return payloadData;
        }

        default:
          const answers = question.answers?.map((item: any) => {
            return item.isNew ? { option: item.option } : item;
          });
          return { ...formDetails?.questions?.[index], ...question, answers };
      }
    });

    const questions = questionsPayload.map((item: any) => {
      if (item.isNew) {
        delete item.isNew;
        delete item.id;
      }
      return item;
    });

    const payload = {
      ...formDetails,
      ...data,
      questions
    };

    const response: any = await dispatch(updateFeedback(payload));
    if (response.payload.status === responseTypes.success) {
      navigate(routes.Feedback);
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          variant: "success",
          setState: setFeedbackDetails({ shouldShow: false }),
          message: "Feedback saved successfully"
        })
      );
    }
  };

  return (
    <>
      <Feedback form="updateFeedback" />
      {questionCategories.length && (
        <div className="edit-feedback-container">
          <div className="feedback-left-container">
            <Typography variant={"h4"} showBold color={tokens.Text.General.PrimaryHeadline}>
              {type}
            </Typography>
            <Typography color={tokens.Text.General.PrimaryBody}>{triggerPoint}</Typography>
          </div>
          <div className="feedback-right-container">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(submitHandler)}>
                <div className="questions-container">
                  {getValues("questions")?.map((question: any, index: number) => (
                    <DynamicInput
                      key={question.id || index + 1}
                      questionIndex={index}
                      {...question}
                      onDelete={deleteInput}
                    />
                  ))}
                  <div className="add-questions-container">
                    <div className="question-add" onClick={addInput}>
                      <Box marginRight={"15px"}>
                        <Add />
                      </Box>
                      <Typography variant="body1" showBold color={tokens.Text.General.Color}>
                        Add New Question
                      </Typography>
                    </div>
                  </div>
                  <div className="right-container-action-buttons">
                    <Button label={"Cancel "} variant="outlined" onClick={() => navigate(routes.Feedback)} />
                    <Button label={`Apply`} disabled={!questions?.length} type={"submit"} />
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      )}
    </>
  );
}
